(() => {
  const config = document.querySelector('script[data-dam-qa-loader]');
  if (!config || window.damCardQuickAddReady) return;
  window.damCardQuickAddReady = true;
  let active = null;
  let busy = false;
  let sequence = 0;
  let syncPromise;
  const text = (key) => config.dataset[key] || '';
  const announcer = document.createElement('span');
  announcer.className = 'visually-hidden';
  announcer.setAttribute('role', 'status');
  announcer.setAttribute('aria-live', 'polite');
  document.body.append(announcer);
  const openCart = () => document.querySelector('theme-drawer#cart-drawer')?.open?.();
  const loadSync = () => (syncPromise ||= import(config.dataset.cartSyncUrl).catch((error) => { syncPromise = null; throw error; }));

  function closeMenu(restoreFocus = false) {
    if (!active) return;
    const { root, panel, trigger } = active;
    panel.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    if (root.isConnected) root.append(panel);
    else panel.remove();
    active = null;
    if (restoreFocus && trigger.isConnected) trigger.focus({ preventScroll: true });
  }

  function positionMenu() {
    if (!active) return;
    if (!active.trigger.isConnected) { closeMenu(); return; }
    const { panel, trigger } = active;
    const box = trigger.getBoundingClientRect();
    const width = Math.min(280, window.innerWidth - 24);
    panel.style.width = `${width}px`;
    const height = panel.getBoundingClientRect().height;
    const left = Math.max(12, Math.min(box.right - width, window.innerWidth - width - 12));
    let top = box.bottom + 8;
    if (top + height > window.innerHeight - 12) top = box.top - height - 8;
    panel.style.left = `${left}px`;
    panel.style.top = `${Math.max(12, top)}px`;
  }

  function restoreButtons(state) {
    state.panel.querySelectorAll('[data-dam-qa-variant]').forEach((button) => {
      button.disabled = button.hasAttribute('data-dam-qa-unavailable') || state.root.dataset.damQaUncertain === 'true';
    });
    state.panel.removeAttribute('aria-busy');
  }

  function setStatus(state, message, showCart = false) {
    const status = state.panel.querySelector('[data-dam-qa-status]');
    status.textContent = message;
    status.hidden = !message;
    state.panel.querySelector('[data-dam-qa-view-cart]').hidden = !showCart;
    announcer.textContent = message;
    positionMenu();
  }

  function showMenu(trigger) {
    if (busy) return;
    if (active?.trigger === trigger) { closeMenu(true); return; }
    closeMenu();
    const root = trigger.closest('[data-dam-card-quick-add]');
    const panel = root?.querySelector('[data-dam-qa-panel]');
    if (!panel) return;
    panel.id ||= `dam-qa-panel-${++sequence}`;
    trigger.setAttribute('aria-controls', panel.id);
    trigger.setAttribute('aria-expanded', 'true');
    active = { root, panel, trigger };
    document.body.append(panel);
    panel.hidden = false;
    restoreButtons(active);
    if (root.dataset.damQaUncertain === 'true') setStatus(active, text('uncertain'), true);
    else setStatus(active, '');
    positionMenu();
    panel.focus({ preventScroll: true });
  }

  async function addVariant(button) {
    if (!active || busy || button.disabled || !active.panel.contains(button)) return;
    const id = button.dataset.damQaVariant;
    if (!/^\d+$/.test(id || '')) return;
    const state = active;
    busy = true;
    let requestStarted = false;
    let rejected = false;
    let added = false;
    state.panel.setAttribute('aria-busy', 'true');
    state.panel.querySelectorAll('[data-dam-qa-variant]').forEach((b) => { b.disabled = true; });
    setStatus(state, text('adding'));
    let telemetry;
    try {
      const api = window.DAMTelemetry;
      const op = api?.begin();
      if (op) telemetry = { api, op, params: { product_id: state.root.dataset.productId,
        variant_id: Number(id), kind: 'ordinary', surface: 'ordinary_quick_add' } };
    } catch {}
    const reportResult = (outcome, errorCode) => {
      try {
        if (telemetry) telemetry.api.record('dam_quick_add_result', { ...telemetry.params, outcome, errorCode }, telemetry.op);
      } catch {}
    };
    const track = window.__diyBehavior?.start('pbn_add');
    try {
      const sync = await loadSync();
      const sections = sync.cartSectionsParam();
      requestStarted = true;
      const response = await fetch(config.dataset.cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], ...(sections ? { sections } : {}) }),
      });
      const payload = await response.json();
      if (!response.ok || payload.status) {
        rejected = response.status >= 400 && response.status < 500;
        throw new Error(typeof payload.description === 'string' ? payload.description : text('failed'));
      }
      added = true;
      track?.('accepted');
      reportResult('accepted');
      closeMenu(true);
      await sync.notifyCartAdd({ addResponse: payload, lines: [{ merchandiseId: id, quantity: 1 }], itemCount: 1 });
      announcer.textContent = text('added');
    } catch (error) {
      if (added) {
        announcer.textContent = text('added');
        openCart();
      } else {
        track?.(rejected ? 'rejected' : 'failed');
        reportResult(rejected ? 'rejected' : 'failed', rejected ? 'http_rejected' : requestStarted ? 'request_failed' : 'runtime_unavailable');
        const uncertain = requestStarted && !rejected;
        if (uncertain) state.root.dataset.damQaUncertain = 'true';
        setStatus(state, uncertain ? text('uncertain') : (error instanceof Error ? error.message : text('failed')), uncertain);
      }
    } finally {
      busy = false;
      restoreButtons(state);
    }
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-dam-qa-open]');
    if (trigger) { event.preventDefault(); event.stopPropagation(); showMenu(trigger); return; }
    const option = event.target.closest('[data-dam-qa-variant]');
    if (option && active?.panel.contains(option)) { event.preventDefault(); event.stopPropagation(); void addVariant(option); return; }
    if (event.target.closest('[data-dam-qa-view-cart]')) { event.preventDefault(); closeMenu(true); openCart(); return; }
    if (active && !busy && !active.panel.contains(event.target)) closeMenu();
  }, true);
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && active && !busy) { event.preventDefault(); closeMenu(true); } });
  let frame;
  const reposition = () => { if (!active) return; cancelAnimationFrame(frame); frame = requestAnimationFrame(positionMenu); };
  window.addEventListener('resize', reposition);
  document.addEventListener('scroll', reposition, true);
  window.addEventListener('pagehide', () => closeMenu());
})();
